import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

const VISITOR_ID_KEY = 'capledger_visitor_id';
const SESSION_ID_KEY = 'capledger_session_id';
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const HEATMAP_BATCH_SIZE = 50;
const HEATMAP_SEND_INTERVAL = 10000; // 10 seconds
const REPLAY_SAVE_INTERVAL = 30000; // 30 seconds

interface HeatmapInteraction {
  session_id: string | null;
  page_path: string;
  event_type: 'click' | 'mousemove' | 'scroll';
  x_position?: number;
  y_position?: number;
  viewport_width: number;
  viewport_height: number;
  scroll_depth?: number;
  element_selector?: string;
  element_text?: string;
  timestamp: string;
}

export const useVisitorTracking = () => {
  const sessionIdRef = useRef<string | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const currentPathRef = useRef<string>('');
  const pageStartTimeRef = useRef<number>(Date.now());
  const heatmapBufferRef = useRef<HeatmapInteraction[]>([]);
  const mouseMoveThrottleRef = useRef<number>(0);
  const replayEventsRef = useRef<any[]>([]);
  const stopRecordingRef = useRef<(() => void) | null>(null);
  const replayIntervalRef = useRef<number | null>(null);

  const sendHeatmapData = async () => {
    if (heatmapBufferRef.current.length === 0) return;

    const dataToSend = [...heatmapBufferRef.current];
    heatmapBufferRef.current = [];

    try {
      await supabase.functions.invoke('track-heatmap', {
        body: { interactions: dataToSend },
      });
    } catch (error) {
      console.error('Error sending heatmap data:', error);
      // Re-add to buffer if failed (up to limit)
      if (heatmapBufferRef.current.length < HEATMAP_BATCH_SIZE) {
        heatmapBufferRef.current.unshift(
          ...dataToSend.slice(0, HEATMAP_BATCH_SIZE - heatmapBufferRef.current.length)
        );
      }
    }
  };

  const saveReplayData = async () => {
    if (!sessionIdRef.current || replayEventsRef.current.length === 0) return;

    const events = [...replayEventsRef.current];
    const duration = Date.now() - pageStartTimeRef.current;

    try {
      await supabase.functions.invoke('save-session-replay', {
        body: {
          session_id: sessionIdRef.current,
          events,
          duration_ms: duration,
        },
      });
      console.log(`Saved ${events.length} replay events`);
    } catch (error) {
      console.error('Error saving replay data:', error);
    }
  };

  const addHeatmapInteraction = (
    interaction: Omit<
      HeatmapInteraction,
      'session_id' | 'viewport_width' | 'viewport_height' | 'timestamp'
    >
  ) => {
    if (!sessionIdRef.current) return;

    heatmapBufferRef.current.push({
      ...interaction,
      session_id: sessionIdRef.current,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      timestamp: new Date().toISOString(),
    });

    // Send if buffer is full
    if (heatmapBufferRef.current.length >= HEATMAP_BATCH_SIZE) {
      sendHeatmapData();
    }
  };

  const getElementSelector = (element: Element): string => {
    if (element.id) return `#${element.id}`;
    if (element.className) {
      const classes = element.className
        .split(' ')
        .filter((c) => c)
        .join('.');
      return element.tagName.toLowerCase() + (classes ? `.${classes}` : '');
    }
    return element.tagName.toLowerCase();
  };

  useEffect(() => {
    // Get or create visitor ID
    let visitorId = localStorage.getItem(VISITOR_ID_KEY);
    if (!visitorId) {
      visitorId = crypto.randomUUID();
      localStorage.setItem(VISITOR_ID_KEY, visitorId);
    }

    // Get or create session ID
    let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
    const lastActivity = parseInt(sessionStorage.getItem('last_activity') || '0');

    // Create new session if expired or doesn't exist
    if (!sessionId || Date.now() - lastActivity > SESSION_TIMEOUT) {
      sessionId = crypto.randomUUID();
      sessionStorage.setItem(SESSION_ID_KEY, sessionId);
      sessionIdRef.current = sessionId;

      // Start new session
      startSession(visitorId, sessionId);

      // Start session recording
      import('rrweb')
        .then(({ record }) => {
          stopRecordingRef.current = record({
            emit(event) {
              replayEventsRef.current.push(event);
            },
            sampling: {
              mousemove: true,
              mouseInteraction: true,
              scroll: 150,
              input: 'last',
            },
          });
          console.log('Session recording started');
        })
        .catch((error) => {
          console.error('Error starting session recording:', error);
        });
    } else {
      sessionIdRef.current = sessionId;
    }

    // Track page views on route change
    const trackPageView = () => {
      if (currentPathRef.current !== window.location.pathname) {
        const previousDuration = Date.now() - pageStartTimeRef.current;

        if (currentPathRef.current) {
          // Update previous page duration
          trackPageViewWithDuration(currentPathRef.current, previousDuration);
        }

        currentPathRef.current = window.location.pathname;
        pageStartTimeRef.current = Date.now();

        // Track new page
        trackPageViewWithDuration(window.location.pathname, 0);
      }
    };

    // Initial page view
    trackPageView();

    // Listen for navigation changes
    window.addEventListener('popstate', trackPageView);

    // Track activity
    const updateActivity = () => {
      lastActivityRef.current = Date.now();
      sessionStorage.setItem('last_activity', Date.now().toString());
    };

    // Track clicks with heatmap data
    const handleClick = (e: MouseEvent) => {
      updateActivity();

      const target = e.target as Element;
      addHeatmapInteraction({
        page_path: window.location.pathname,
        event_type: 'click',
        x_position: e.clientX,
        y_position: e.clientY + window.scrollY,
        element_selector: getElementSelector(target),
        element_text: target.textContent?.substring(0, 100) || undefined,
      });
    };

    // Track mouse movement (throttled)
    const handleMouseMove = (e: MouseEvent) => {
      updateActivity();

      const now = Date.now();
      if (now - mouseMoveThrottleRef.current < 500) return; // Throttle to once per 500ms
      mouseMoveThrottleRef.current = now;

      addHeatmapInteraction({
        page_path: window.location.pathname,
        event_type: 'mousemove',
        x_position: e.clientX,
        y_position: e.clientY + window.scrollY,
      });
    };

    // Track scroll depth
    const handleScroll = () => {
      updateActivity();

      const scrollDepth = Math.round(
        (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
      );

      addHeatmapInteraction({
        page_path: window.location.pathname,
        event_type: 'scroll',
        scroll_depth: scrollDepth,
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('keydown', updateActivity);
    window.addEventListener('click', handleClick);
    window.addEventListener('scroll', handleScroll);

    // Periodic send of heatmap data
    const heatmapInterval = setInterval(sendHeatmapData, HEATMAP_SEND_INTERVAL);

    // Periodic save of replay data
    replayIntervalRef.current = setInterval(
      saveReplayData,
      REPLAY_SAVE_INTERVAL
    ) as unknown as number;

    // End session on page unload
    const endSession = () => {
      // Stop recording
      if (stopRecordingRef.current) {
        stopRecordingRef.current();
        stopRecordingRef.current = null;
      }

      // Save replay data
      saveReplayData();

      // Send any remaining heatmap data
      if (heatmapBufferRef.current.length > 0) {
        sendHeatmapData();
      }

      if (sessionIdRef.current) {
        const duration = Date.now() - pageStartTimeRef.current;
        navigator.sendBeacon(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track-visitor`,
          JSON.stringify({
            action: 'end_session',
            sessionId: sessionIdRef.current,
            durationMs: duration,
          })
        );
      }
    };

    window.addEventListener('beforeunload', endSession);

    return () => {
      clearInterval(heatmapInterval);
      if (replayIntervalRef.current) {
        clearInterval(replayIntervalRef.current);
      }
      if (stopRecordingRef.current) {
        stopRecordingRef.current();
      }
      window.removeEventListener('popstate', trackPageView);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('keydown', updateActivity);
      window.removeEventListener('click', handleClick);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('beforeunload', endSession);
    };
  }, []);

  const startSession = async (visitorId: string, sessionId: string) => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      await supabase.functions.invoke('track-visitor', {
        body: {
          action: 'start_session',
          visitorId,
          sessionId,
          userId: user?.id,
          pagePath: window.location.pathname,
          pageTitle: document.title,
          referrer: document.referrer,
          device: /mobile/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
          browser: getBrowser(),
          os: getOS(),
          utmSource: new URLSearchParams(window.location.search).get('utm_source'),
          utmMedium: new URLSearchParams(window.location.search).get('utm_medium'),
          utmCampaign: new URLSearchParams(window.location.search).get('utm_campaign'),
        },
      });
    } catch (error) {
      console.error('Error starting session:', error);
    }
  };

  const trackPageViewWithDuration = async (path: string, durationMs: number) => {
    if (!sessionIdRef.current) return;

    try {
      await supabase.functions.invoke('track-visitor', {
        body: {
          action: 'track_pageview',
          sessionId: sessionIdRef.current,
          pagePath: path,
          pageTitle: document.title,
          referrer: document.referrer,
          durationMs,
        },
      });
    } catch (error) {
      console.error('Error tracking pageview:', error);
    }
  };

  const trackEvent = async (
    eventType: string,
    eventCategory?: string,
    eventLabel?: string,
    eventValue?: any
  ) => {
    if (!sessionIdRef.current) return;

    try {
      await supabase.functions.invoke('track-visitor', {
        body: {
          action: 'track_event',
          sessionId: sessionIdRef.current,
          eventType,
          eventCategory,
          eventLabel,
          eventValue,
          pagePath: window.location.pathname,
        },
      });
    } catch (error) {
      console.error('Error tracking event:', error);
    }
  };

  return { trackEvent };
};

function getBrowser() {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  if (ua.includes('Edge')) return 'Edge';
  return 'Unknown';
}

function getOS() {
  const ua = navigator.userAgent;
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Mac')) return 'MacOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iOS')) return 'iOS';
  return 'Unknown';
}
